// @spec GHG-STR-058
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, type SelectOption } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGHGComplianceStatus,
  useGHGPolicies,
  useGHGProcedures,
} from '@/features/ghg/api/use-policy-integration';
import {
  GHGComplianceStatusCard,
  GHGPoliciesEmptyState,
  GHGPolicyCard,
  GHGProceduresSection,
} from '@/features/ghg/components';
import { useGHGRole } from '@/features/ghg/hooks/useGHGRole';
import type {
  GHGCategory,
  GHGPolicyListFilters,
  PolicyStatus,
} from '@/features/ghg/types/policy-integration';
import { GHG_CATEGORY_LABELS, POLICY_STATUS_LABELS } from '@/features/ghg/types/policy-integration';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/policies')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.ghgPolicies'),
  }),
  component: GHGPoliciesPage,
});

/**
 * GHG Policies page for viewing and managing GHG-related policies.
 *
 * Displays:
 * - GHG Protocol compliance status summary
 * - List of policies linked to GHG Protocol or tagged with 'ghg'
 * - GHG-related procedures with scheduling information
 *
 * Allows GHG Managers to:
 * - Filter policies by status and category
 * - Navigate to Policy Hub for editing
 */
function GHGPoliciesPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { isManager: isGHGManager } = useGHGRole();

  // Filter state
  const [filters, setFilters] = useState<GHGPolicyListFilters>({
    page: 1,
    pageSize: 12,
  });
  const [searchInput, setSearchInput] = useState('');

  // Select options
  const statusOptions: SelectOption[] = [
    { value: 'all', label: t('policiesPage.allStatus') },
    ...Object.entries(POLICY_STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  const categoryOptions: SelectOption[] = [
    { value: 'all', label: t('policiesPage.allCategories') },
    ...Object.entries(GHG_CATEGORY_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  // Fetch data
  const {
    data: policiesData,
    isLoading: policiesLoading,
    error: policiesError,
  } = useGHGPolicies(filters);

  const { data: complianceStatus, isLoading: complianceLoading } = useGHGComplianceStatus();

  const { data: proceduresData, isLoading: proceduresLoading } = useGHGProcedures();

  // Handlers
  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: searchInput || undefined,
      page: 1,
    }));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleStatusFilter = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      status: value === 'all' ? undefined : (value as PolicyStatus),
      page: 1,
    }));
  };

  const handleCategoryFilter = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      category: value === 'all' ? undefined : (value as GHGCategory),
      page: 1,
    }));
  };

  const handleViewPolicy = (policyId: string) => {
    // Navigate to policy detail in GHG context (could be a modal or detail page)
    // For now, navigate to Policy Hub
    void navigate({ to: '/policies/$policyId', params: { policyId } });
  };

  const handleEditPolicy = (policyId: string) => {
    // Navigate to Policy Hub edit page
    void navigate({ to: '/policies/$policyId', params: { policyId } });
  };

  const handleCreatePolicy = () => {
    // Navigate to Policy Hub create page with GHG tag pre-selected
    void navigate({ to: '/policies/create' });
  };

  const handleStartProcedure = (procedureId: string) => {
    // Navigate to procedure execution
    void navigate({
      to: '/policies/procedures/$instanceId/execute',
      params: { instanceId: procedureId },
    });
  };

  const handleViewProcedure = (_procedureId: string) => {
    // Navigate to procedure detail in procedures index for now
    void navigate({ to: '/procedures' });
  };

  // Loading state for policies grid
  const PoliciesLoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Skeleton key={i} className="h-48 w-full" />
      ))}
    </div>
  );

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('policiesPage.heading')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('policiesPage.description')}
            </p>
          </div>
          {isGHGManager && (
            <Button onClick={handleCreatePolicy}>
              <Plus className="h-4 w-4 mr-2" />
              {t('policiesPage.newPolicy')}
            </Button>
          )}
        </header>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Compliance Status */}
          <aside className="lg:col-span-1 space-y-6">
            <GHGComplianceStatusCard status={complianceStatus} isLoading={complianceLoading} />
          </aside>

          {/* Main Content - Policies */}
          <div className="lg:col-span-3 space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 flex gap-2">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('policiesPage.searchPlaceholder')}
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                    }}
                    onKeyDown={handleSearchKeyDown}
                    className="pl-9"
                  />
                </div>
                <Button variant="outline" onClick={handleSearch}>
                  {t('policiesPage.search')}
                </Button>
              </div>
              <div className="flex gap-2">
                <Select
                  value={filters.status ?? 'all'}
                  onChange={handleStatusFilter}
                  options={statusOptions}
                  className="w-[140px]"
                />
                <Select
                  value={filters.category ?? 'all'}
                  onChange={handleCategoryFilter}
                  options={categoryOptions}
                  className="w-[160px]"
                />
              </div>
            </div>

            {/* Policies Grid */}
            {policiesLoading ? (
              <PoliciesLoadingSkeleton />
            ) : policiesError ? (
              <div className="p-8 text-center text-destructive">
                {t('policiesPage.failedToLoad')} {policiesError.message}
              </div>
            ) : !policiesData || policiesData.items.length === 0 ? (
              <GHGPoliciesEmptyState
                onCreatePolicy={handleCreatePolicy}
                canManage={isGHGManager}
              />
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {policiesData.items.map((policy) => (
                    <GHGPolicyCard
                      key={policy.id}
                      policy={policy}
                      onView={handleViewPolicy}
                      onEdit={handleEditPolicy}
                      canManage={isGHGManager}
                    />
                  ))}
                </div>

                {/* Pagination info */}
                <div className="text-sm text-muted-foreground text-center">
                  {t('policiesPage.showingCount', { count: policiesData.items.length, total: policiesData.total })}
                </div>
              </>
            )}

            {/* Procedures Section */}
            <GHGProceduresSection
              procedures={proceduresData?.items ?? []}
              isLoading={proceduresLoading}
              onStartProcedure={handleStartProcedure}
              onViewProcedure={handleViewProcedure}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
